export const ACCESS_LEVELS = {
  STAFF: 'staff',
  MANAGER: 'manager',
  SUPER_ADMIN: 'super_admin'
} as const;

export type AccessLevel = typeof ACCESS_LEVELS[keyof typeof ACCESS_LEVELS];

// Define which sections each access level can access
export const SECTION_PERMISSIONS = {
  // Sections accessible to ALL roles
  DASHBOARD: { name: 'Dashboard', allowedRoles: ['staff', 'manager', 'super_admin'] as AccessLevel[] },
  MEMBERS: { name: 'Members', allowedRoles: ['staff', 'manager', 'super_admin'] as AccessLevel[] },
  MEMBERSHIPS: { name: 'Memberships', allowedRoles: ['staff', 'manager', 'super_admin'] as AccessLevel[] },
  GIFT_CARDS: { name: 'Gift Cards', allowedRoles: ['staff', 'manager', 'super_admin'] as AccessLevel[] },
  GIFT_CARDS_MANAGE: { name: 'Gift Cards (Manage)', allowedRoles: ['manager', 'super_admin'] as AccessLevel[] },
  PT_SESSIONS: { name: 'PT Sessions', allowedRoles: ['staff', 'manager', 'super_admin'] as AccessLevel[] },
  PT_PACKS: { name: 'PT Packs', allowedRoles: ['staff', 'manager', 'super_admin'] as AccessLevel[] },
  PERSONAL_TRAINING: { name: 'Personal Training', allowedRoles: ['staff', 'manager', 'super_admin'] as AccessLevel[] },
  CLASSES: { name: 'Classes', allowedRoles: ['staff', 'manager', 'super_admin'] as AccessLevel[] },
  SPACES: { name: 'Spaces', allowedRoles: ['staff', 'manager', 'super_admin'] as AccessLevel[] },
  LOCKERS: { name: 'Lockers', allowedRoles: ['staff', 'manager', 'super_admin'] as AccessLevel[] },
  CHECK_INS: { name: 'Check-ins', allowedRoles: ['staff', 'manager', 'super_admin'] as AccessLevel[] },
  BIRTHDAYS: { name: 'Birthdays', allowedRoles: ['staff', 'manager', 'super_admin'] as AccessLevel[] },
  AGENT: { name: 'Agent', allowedRoles: ['staff', 'manager', 'super_admin'] as AccessLevel[] },

  // Sections accessible ONLY to super_admin
  ADMIN_USERS: { name: 'Admin Users', allowedRoles: ['super_admin'] as AccessLevel[] },
  MEMBERSHIP_TYPES: { name: 'Membership Types', allowedRoles: ['super_admin'] as AccessLevel[] },
  CLASS_CATEGORIES: { name: 'Class Categories', allowedRoles: ['super_admin'] as AccessLevel[] },
  CLASS_LOCATIONS: { name: 'Class Locations', allowedRoles: ['super_admin'] as AccessLevel[] },
  COACHES: { name: 'Coaches', allowedRoles: ['super_admin'] as AccessLevel[] },
  EMPLOYEES: { name: 'Employees', allowedRoles: ['super_admin'] as AccessLevel[] },
  INVENTORY: { name: 'Inventory', allowedRoles: ['super_admin'] as AccessLevel[] },
  PT_PACK_TYPES: { name: 'PT Pack Types', allowedRoles: ['super_admin'] as AccessLevel[] },
  ACTIVITY_LOGS: { name: 'Activity Logs', allowedRoles: ['super_admin'] as AccessLevel[] },
  REPORTS: { name: 'Reports', allowedRoles: ['super_admin'] as AccessLevel[] },
  STYLE_BOOK: { name: 'Style Book', allowedRoles: ['super_admin'] as AccessLevel[] },
  ODOO: { name: 'Odoo', allowedRoles: ['super_admin'] as AccessLevel[] },
  BPA: { name: 'BPA', allowedRoles: ['staff', 'manager', 'super_admin'] as AccessLevel[] }
} as const;

export type SectionKey = keyof typeof SECTION_PERMISSIONS;

// Helper function to check if user has access to a section
export function hasAccess(userAccessLevel: AccessLevel, section: SectionKey): boolean {
  return SECTION_PERMISSIONS[section]?.allowedRoles?.includes(userAccessLevel) || false;
}

// Helper function to get all accessible sections for a role
export function getAccessibleSections(userAccessLevel: AccessLevel): SectionKey[] {
  return Object.keys(SECTION_PERMISSIONS).filter(section => 
    hasAccess(userAccessLevel, section as SectionKey)
  ) as SectionKey[];
}

// Access level descriptions for the Admin Users page
export const ACCESS_LEVEL_DESCRIPTIONS = {
  [ACCESS_LEVELS.STAFF]: {
    name: 'Staff',
    description: 'Basic access to manage day-to-day operations. Cannot cancel or delete memberships.',
    sections: ['Dashboard', 'Members', 'Memberships', 'Personal Training', 'Classes', 'Spaces', 'Lockers', 'Check-ins', 'Birthdays', 'Agent']
  },
  [ACCESS_LEVELS.MANAGER]: {
    name: 'Manager',
    description: 'Day-to-day operations plus the ability to cancel and delete memberships.',
    sections: ['Dashboard', 'Members', 'Memberships', 'Personal Training', 'Classes', 'Spaces', 'Lockers', 'Check-ins', 'Birthdays', 'Agent']
  },
  [ACCESS_LEVELS.SUPER_ADMIN]: {
    name: 'Super Admin',
    description: 'Full access to all sections including system administration',
    sections: ['All sections', 'Admin Users', 'Membership Types', 'Class Categories', 'Class Locations', 'Coaches', 'Employees', 'Inventory', 'Activity Logs', 'Reports', 'Style Book', 'Odoo']
  }
} as const; 